import {TableStore} from 'app/store/TableStore';
import {UserStore} from 'app/store/UserStore';
import {CurrentTimeStore} from 'stores/current_time_store';
import Line from './Line';
import Get from 'app/utils/Get';
import {WHITE} from 'app/lines/line_helpers';
import LineInterface from 'app/interfaces/LineInterface';
import {isNestedLine} from 'app/lines/isNestedLine';
import {isMainLine} from 'app/lines/isMainLine';
import GameInterface from 'app/interfaces/GameInterface';

type UserBetsType = {
    on: Map<number, Array<number>>
    root: Array<number>
};

interface LinesServiceConstructor {
    url: string
    userStore: UserStore
    currentTimeStore: CurrentTimeStore
}

export type Lines = Record<number, Line>;
type Callback = (parents: Lines, results: boolean) => void;

type OrphanNestedLine = {
    line: LineInterface
    addedAt: number
};

export const ORPHAN_NESTED_LINES_TTL_MS = 120000;

interface ApiResponse {
    bets: LineInterface[],
    nested_bets: LineInterface[]
    user_bets: UserBetsType
    user_time: number
    ut: number
    current_filters?: GameInterface[]
}

export default class LinesService {
    constructor({url, userStore, currentTimeStore}: LinesServiceConstructor) {
        this.url = url;
        this.userStore = userStore;
        this.currentTimeStore = currentTimeStore;
    }

    userStore: UserStore;

    tableStore: TableStore;

    currentTimeStore: CurrentTimeStore;

    url: string;

    parents: Lines = {};

    results_parents: Lines = {};

    user_bets: UserBetsType = {on: {} as UserBetsType['on'], root: []};

    update_time = 0;

    user_time = 0;

    pendingLines: LineInterface[] = [];

    private orphanNestedLines = new Map<number, Map<number, OrphanNestedLine>>();

    setBets(newLines: LineInterface[], results: boolean): void {
        newLines.forEach(newLine => {
            if (this.update_time < newLine.ut) {
                this.update_time = newLine.ut;
            }
            this.setBet(newLine, results);
        });
    }

    setBet(newLine: LineInterface, results: boolean): void {
        const oldLine = this.findOldLine(newLine, results);

        if (newLine.color === WHITE && !results && !isNestedLine(newLine)) {
            this.deleteMainLine(newLine, false);
        } else if (newLine.color !== WHITE && results) {
            this.deleteMainLine(newLine, true);
        } else if (oldLine) {
            if (this.canDeleteLine(newLine)) {
                this.deleteOldLine(newLine, results);
            } else {
                oldLine.update(newLine);
            }
        } else if (this.canDeleteLine(newLine)) {
            if (!results) {
                this.removeOrphanNestedLine(newLine);
            }
        } else {
            this.createNewLine(newLine, results);
        }
    }

    // eslint-disable-next-line max-params,max-statements
    updateBetsHandler(data: ApiResponse, callback: Callback, results = false, forceUpdate = false): void {
        this.currentTimeStore.setTime(data.user_time);
        this.user_time = data.user_time;
        let update = this.update_time > 0;

        if (data.ut === 0 && !forceUpdate) {
            update = false;
            const parents = this.getParents(results);
            const newParents = {};

            if (this.userStore.user.light_mode) {
                this.tableStore.openedLines.forEach(id => {
                    if (parents[id]) {
                        newParents[id] = parents[id];
                    }
                });
            }
            this.setParents(newParents, results);
        }
        if (data.bets) {
            this.setBets(data.bets, results);
        }
        if (data.nested_bets) {
            this.setBets(data.nested_bets, results);
        }
        if (data.user_bets) {
            this.user_bets = data.user_bets;
        }
        if (data.current_filters && data.current_filters.length > 0 && this.tableStore.filterStore) {
            const currentIds = this.tableStore.filterStore.games.map(g => g.id).join();
            const newIds = data.current_filters.map(g => g.id).join();

            if (currentIds !== newIds) {
                this.tableStore.filterStore.setGames(data.current_filters);
            }
        }
        if (!callback) {
            return;
        }
        callback(this.getParents(results), results);

        if (this.userStore.user.light_mode && !update) {
            this.tableStore.clearLoadedLinesIds();
            if (this.tableStore.openedLines.length > 0) {
                this.update_line(this.tableStore.openedLines);
            }
        }
    }

    update_bets(callback: Callback, results = false, allLines = false): Promise<void> {
        return new Get({params: {
            active: !results,
            st: this.user_time,
            ut: allLines ? 0 : this.update_time
        },
        url: this.url})
            .execute()
            .then(response => response.json())
            .then(data => this.updateBetsHandler(data, callback, results));
    }

    update_line(line_id: number | number[], callback?: Callback, results = false): Promise<void> {
        if (!Array.isArray(line_id) && !this.getParents(results)[line_id]) {
            return Promise.resolve();
        }

        return new Get({
            params: {
                line_id,
                st: this.user_time,
                ut: this.update_time
            },
            url: this.url
        })
            .execute()
            .then((response): Promise<ApiResponse> => response.json())
            .then(data => {
                this.tableStore.addLoadedLineId(line_id);
                this.updateBetsHandler(data, callback, results, true);
            });
    }

    listen(payload: LineInterface): void {
        const oldLine = this.findOldLine(payload, false);
        const colorChanged = oldLine && 'real_color' in payload &&
            oldLine.asLine().color !== payload.real_color;

        if (colorChanged && 'real_color' in payload) {
            payload.color = payload.real_color;
        }

        const isExistingLineUpdate = oldLine && !this.canDeleteLine(payload);

        this.setBet(payload, false);
        if (!isExistingLineUpdate && this.tableStore && payload.root_line_id && this.userStore?.user?.experimental_features === true) {
            const parents = this.tableStore.results ? this.results_parents : this.parents;
            this.tableStore.updateBetsCallback(parents, this.tableStore.results);
        }
    }

    private canDeleteLine = (line: LineInterface): boolean => line.deleted || line.hidden;

    private findOldLine = (line: LineInterface, results: boolean): Line => {
        const rootLine = this.getParents(results)[line.root_line_id];

        if (!rootLine || isMainLine(line)) {
            return rootLine;
        }
        return rootLine.allNestedLines[line.id];
    };

    private createNewLine = (line: LineInterface, results: boolean): void => {
        if (isMainLine(line)) {
            const rootLine = new Line(line);

            this.getParents(results)[line.id] = rootLine;
            if (!results) {
                this.attachOrphanNestedLines(rootLine);
            }
            return;
        }

        const rootLine = this.findRootLine(line, results);

        if (!rootLine) {
            if (!results) {
                this.addOrphanNestedLine(line);
            }
            return;
        }
        this.createNestedLine(line, rootLine);
        this.createPendingLines(rootLine);
    };

    private deleteOldLine = (line: LineInterface, results: boolean): void => {
        this.tableStore.removeLoadedLine(line.root_line_id);

        const rootLine = this.findRootLine(line, results);

        if (!rootLine) {
            return;
        }

        if (isMainLine(line)) {
            delete this.getParents(results)[line.id];
            return;
        }
        this.deleteNestedLine(line, rootLine);
    };

    private deleteMainLine = (line: LineInterface, results: boolean): void => {
        this.tableStore.removeLoadedLine(line.root_line_id);
        delete this.getParents(results)[line.id];
    };

    private getParents = results => results ? this.results_parents : this.parents;

    private setParents(newParents, results) {
        if (results) {
            this.results_parents = newParents;
        } else {
            this.parents = newParents;
        }
    }

    private findRootLine = (line: LineInterface, results: boolean): Line => this.getParents(
        results
    )[line.root_line_id];

    private createNestedLine = (line: LineInterface, rootLine: Line): void => {
        const parent = this.findParent(line, rootLine);
        const res = new Line(line, parent) as unknown as LineInterface;

        if (parent.id === line.parent_line_id) {
            rootLine.allNestedLines[line.id] = res;
            parent.nested_bets[line.id] = res;
        } else {
            this.pendingLines = this.pendingLines.filter(pendingLine => pendingLine.id !== line.id);
            this.pendingLines.push(line);
        }
    };

    private createPendingLines(rootLine: Line) {
        if (this.pendingLines.length === 0) {
            return;
        }

        const queue = this.pendingLines;

        this.pendingLines = [];
        queue.forEach(pendingLine => {
            if (this.findParentLine(pendingLine, rootLine)) {
                this.createNestedLine(pendingLine, rootLine);
            } else {
                this.pendingLines.push(pendingLine);
            }
        });
    }

    private addOrphanNestedLine = (line: LineInterface): void => {
        this.pruneOrphanNestedLines();

        let waiting = this.orphanNestedLines.get(line.root_line_id);

        if (!waiting) {
            waiting = new Map<number, OrphanNestedLine>();
            this.orphanNestedLines.set(line.root_line_id, waiting);
        }
        waiting.set(line.id, {addedAt: Date.now(), line});
    };

    private attachOrphanNestedLines = (rootLine: Line): void => {
        this.pruneOrphanNestedLines();

        const rootId = rootLine.asLine().id;
        const waiting = this.orphanNestedLines.get(rootId);

        if (!waiting) {
            return;
        }
        this.orphanNestedLines.delete(rootId);
        waiting.forEach(({line}) => {
            this.createNestedLine(line, rootLine);
        });
        this.createPendingLines(rootLine);
    };

    private removeOrphanNestedLine = (line: LineInterface): void => {
        if (isMainLine(line)) {
            this.orphanNestedLines.delete(line.id);
            return;
        }
        this.pendingLines = this.pendingLines.filter(pendingLine => pendingLine.id !== line.id);

        const waiting = this.orphanNestedLines.get(line.root_line_id);

        if (!waiting) {
            return;
        }
        waiting.delete(line.id);
        if (waiting.size === 0) {
            this.orphanNestedLines.delete(line.root_line_id);
        }
    };

    private pruneOrphanNestedLines = (): void => {
        const now = Date.now();

        this.orphanNestedLines.forEach((waiting, rootId) => {
            waiting.forEach(({addedAt}, id) => {
                if (now - addedAt > ORPHAN_NESTED_LINES_TTL_MS) {
                    waiting.delete(id);
                }
            });
            if (waiting.size === 0) {
                this.orphanNestedLines.delete(rootId);
            }
        });
    };


    private deleteNestedLine = (line: LineInterface, rootLine: Line): void => {
        const parent = this.findParent(line, rootLine);
        delete parent.nested_bets[line.id];
        delete rootLine.allNestedLines[line.id];
    };

    private findParent = (line: LineInterface, rootLine: Line): LineInterface => {
        let parent = rootLine.allNestedLines[line.game_id];
        if (!parent) {
            parent = rootLine;
        }
        return parent;
    };

    private findParentLine = (line: LineInterface, rootLine: Line): LineInterface => {
        const parent = rootLine.allNestedLines[line.game_id];

        return parent;
    };
}
